import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Clock, CheckCircle2, AlertTriangle, User, ShieldCheck, Loader2, FileText,
} from 'lucide-react';

import Button from '../../../components/ui/Button';
import TextInput from '../../../components/ui/TextInput.jsx';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Chip = styled.button`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  padding: 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary.main : '#E8ECF0')};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary.main : '#fff')};
  color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.text.dark.little)};
  transition: all 0.15s;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const IconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const TaskTitle = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 6px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const Sla = styled.span`
  color: ${({ theme, $overdue }) => ($overdue ? theme.colors.auxiliar.danger : theme.colors.text.dark.little)};
  font-weight: ${({ $overdue, theme }) => ($overdue ? theme.metrics.font.weight.semiBold : 'inherit')};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const NoteBox = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Empty = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

// Şablonun documentTypes bağı olan görevlerde: hangi belgelerin istendiğini
// göster ve kullanıcıyı Belgeler sekmesine taşı — evrak görevi artık "boş
// onay" değil, yüklemeye yönlendiren bir adım.
const DocHint = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(2, 145, 239, 0.06);
  border: 1px solid #BFD9F2;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DocLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12.5px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.main};
  text-decoration: underline;
`;

const OPEN_STATUSES = ['assigned', 'in_progress', 'hr_approval_pending'];

const statusVisual = (status, overdue) => {
  if (status === 'completed') return { Icon: CheckCircle2, bg: 'rgba(2,211,191,0.12)', color: '#01A899' };
  if (status === 'sla_breached' || overdue) return { Icon: AlertTriangle, bg: 'rgba(229,57,53,0.10)', color: '#E53935' };
  return { Icon: Clock, bg: 'rgba(2,145,239,0.10)', color: '#0291EF' };
};

const fmtDate = (iso, lang) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(lang || undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function TasksTab({ tasks, onStart, onComplete, onGoDocuments }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [filter, setFilter] = useState('open');
  const [busyId, setBusyId] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [note, setNote] = useState('');

  const now = Date.now();
  const isOverdue = (task) =>
    task.status !== 'completed' && task.slaDueAt && new Date(task.slaDueAt).getTime() < now;

  const filtered = useMemo(() => {
    const list = Array.isArray(tasks) ? tasks : [];
    if (filter === 'completed') return list.filter((tk) => tk.status === 'completed');
    if (filter === 'breached') return list.filter((tk) => tk.status === 'sla_breached' || isOverdue(tk));
    if (filter === 'open') return list.filter((tk) => OPEN_STATUSES.includes(tk.status));
    return list;
  }, [tasks, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const runStart = async (id) => {
    setBusyId(id);
    await onStart(id);
    setBusyId(null);
  };

  const runComplete = async (id) => {
    setBusyId(id);
    const ok = await onComplete(id, note);
    setBusyId(null);
    if (ok) {
      setCompletingId(null);
      setNote('');
    }
  };

  const filters = [
    { key: 'open', label: t('myPreboarding.tasks.filter.open') },
    { key: 'completed', label: t('myPreboarding.tasks.filter.completed') },
    { key: 'breached', label: t('myPreboarding.tasks.filter.breached') },
    { key: 'all', label: t('myPreboarding.tasks.filter.all') },
  ];

  return (
    <Wrap>
      <FilterRow>
        {filters.map((f) => (
          <Chip key={f.key} $active={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
          </Chip>
        ))}
      </FilterRow>

      {filtered.length === 0 && <Empty>{t('myPreboarding.tasks.empty')}</Empty>}

      {filtered.map((task) => {
        const overdue = isOverdue(task);
        const v = statusVisual(task.status, overdue);
        const slaText = fmtDate(task.slaDueAt, lang);
        const canStart = task.status === 'assigned';
        const canComplete = task.status === 'assigned' || task.status === 'in_progress';
        const isBusy = busyId === task.id;

        return (
          <Card key={task.id}>
            <Row>
              <IconWrap $bg={v.bg} $color={v.color}>
                <v.Icon size={18} />
              </IconWrap>
              <Body>
                <TaskTitle>{task.title}</TaskTitle>
                <Meta>
                  <MetaPill>
                    <User size={13} />
                    {t(`myPreboarding.tasks.owner.${task.ownerRole}`, { defaultValue: task.ownerRole })}
                  </MetaPill>
                  <Badge $bg={v.bg} $color={v.color}>
                    {t(`myPreboarding.tasks.status.${task.status}`, { defaultValue: task.status })}
                  </Badge>
                  {task.isMandatory && (
                    <Badge $bg="rgba(255,193,7,0.14)" $color="#C79100">
                      {t('myPreboarding.tasks.mandatory')}
                    </Badge>
                  )}
                  {task.requiresHrApproval && task.status !== 'completed' && (
                    <MetaPill>
                      <ShieldCheck size={13} />
                      {t('myPreboarding.tasks.requiresHrApproval')}
                    </MetaPill>
                  )}
                  {slaText && (
                    <Sla $overdue={overdue}>
                      {t('myPreboarding.tasks.slaDue')}: {slaText}
                    </Sla>
                  )}
                </Meta>

                {Array.isArray(task.linkedDocumentTypes) && task.linkedDocumentTypes.length > 0 && (
                  <DocHint>
                    <FileText size={14} />
                    <span>
                      {t('myPreboarding.tasks.linkedDocuments')}: {task.linkedDocumentTypes
                        .map((dt) => t(`myPreboarding.documents.type.${dt}`, { defaultValue: dt }))
                        .join(', ')}
                    </span>
                    {task.status !== 'completed' && onGoDocuments && (
                      <DocLink type="button" onClick={onGoDocuments}>
                        {t('myPreboarding.tasks.goToDocuments')}
                      </DocLink>
                    )}
                  </DocHint>
                )}

                {(canStart || canComplete) && (
                  completingId === task.id ? (
                    <NoteBox>
                      <TextInput
                        type="textarea"
                        rows={3}
                        label={t('myPreboarding.tasks.completeNote')}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                      <Actions>
                        <Button variant="primary" disabled={isBusy} onClick={() => runComplete(task.id)}>
                          {isBusy ? <Loader2 size={16} /> : t('myPreboarding.tasks.action.confirmComplete')}
                        </Button>
                        <Button
                          variant="ghost"
                          disabled={isBusy}
                          onClick={() => { setCompletingId(null); setNote(''); }}
                        >
                          {t('common.cancel')}
                        </Button>
                      </Actions>
                    </NoteBox>
                  ) : (
                    <Actions>
                      {canStart && (
                        <Button variant="outline" disabled={isBusy} onClick={() => runStart(task.id)}>
                          {isBusy ? <Loader2 size={16} /> : t('myPreboarding.tasks.action.start')}
                        </Button>
                      )}
                      {canComplete && (
                        <Button
                          variant="primary"
                          disabled={isBusy}
                          onClick={() => { setCompletingId(task.id); setNote(''); }}
                        >
                          {t('myPreboarding.tasks.action.complete')}
                        </Button>
                      )}
                    </Actions>
                  )
                )}
              </Body>
            </Row>
          </Card>
        );
      })}
    </Wrap>
  );
}
